import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { useSAL1030_R01Store } from "./Store_SAL1030_R01";
import { defaultSAL1030_R01Dto, SAL1030_R01Dto } from "./Dto_SAL1030_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL1030_R01-grid-column-state-v2';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL1030_R01Dto> | null>;
}

export const Table_SAL1030_R01 = (props: Props) => {
	const { gridRef } = props;

	const { 
		SAL1030_R01List, 
		selectedSAL1030_R01, 
		setSelectedSAL1030_R01, 
		initSelectedSAL1030_R01,
		columnState, 
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useSAL1030_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SAL1030_R01List,
		defaultGridData: defaultSAL1030_R01Dto,
		selectedRow: selectedSAL1030_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "순번",
		compNm: '사업장',
		orderDtDisplay: '날짜',
		customCd: '거래처',
		customNm: '상호',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		itemUnit: '단위',
		itemStatus: '등급',
		orderQty: '수량',
		cancelQty: '취소량',
		orderUnit: '단위',
		orderPrice: '단가',
		dcAmtDisplay: '할인액',
		itemAmt: '금액',
		shippedQty: '출고량',
		remainingAmt: '미출고금액',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'itemNm')(params), []);

	const baseColumnDefs: ColDef<SAL1030_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '날짜', colId: 'orderDtDisplay', field: 'orderDtDisplay', width: 110,
			cellStyle: (p) => getCellStyle(p, 'orderDtDisplay'),
			headerClass: aggridHeaderClass,
			comparator: cellDateComparator,
		},
		{
			headerName: '거래처', colId: 'customCd', field: 'customCd', width: 100,
			cellStyle: (p) => getCellStyle(p, 'customCd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '상호', colId: 'customNm', field: 'customNm', width: 150,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 150,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 150,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '등급', colId: 'itemStatus', field: 'itemStatus', width: 80,
			cellStyle: (p) => getCellStyle(p, 'itemStatus'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '수량', colId: 'orderQty', field: 'orderQty', width: 100,
			cellStyle: (p) => getCellStyle(p, 'orderQty'),
			headerClass: aggridHeaderClass,
			type: 'rightAligned',
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value ? Math.floor(params.value).toLocaleString() : '0',
		},
		{
			headerName: '취소량', colId: 'cancelQty', field: 'cancelQty', width: 100,
			cellStyle: (p) => getCellStyle(p, 'cancelQty'),
			headerClass: aggridHeaderClass,
			type: 'rightAligned',
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value ? Math.floor(params.value).toLocaleString() : '0',
		},
		{
			headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 80,
			cellStyle: (p) => getCellStyle(p, 'itemUnit'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '단가', colId: 'orderPrice', field: 'orderPrice', width: 100,
			cellStyle: (p) => getCellStyle(p, 'orderPrice'),
			headerClass: aggridHeaderClass,
			type: 'rightAligned',
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '할인액', colId: 'dcAmtDisplay', field: 'dcAmtDisplay', width: 100,
			cellStyle: (p) => getCellStyle(p, 'dcAmtDisplay'),
			headerClass: aggridHeaderClass,
			type: 'rightAligned',
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '금액', colId: 'itemAmt', field: 'itemAmt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'itemAmt'),
			headerClass: aggridHeaderClass,
			type: 'rightAligned',
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '출고량', colId: 'shippedQty', field: 'shippedQty', width: 100,
			cellStyle: (p) => getCellStyle(p, 'shippedQty'),
			headerClass: aggridHeaderClass,
			type: 'rightAligned',
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value ? Math.floor(params.value).toLocaleString() : '0',
		},
		{
			headerName: '미출고금액', colId: 'remainingAmt', field: 'remainingAmt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'remainingAmt'),
			headerClass: aggridHeaderClass,
			type: 'rightAligned',
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL1030_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL1030_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<SAL1030_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		const isSelected = selectedSAL1030_R01?.valueIndex === rowData.valueIndex;
		if (isSelected) {
			setSelectedSAL1030_R01(undefined);
		} else {
			setSelectedSAL1030_R01(rowData);
		}
	}, [selectedSAL1030_R01, setSelectedSAL1030_R01]);

	const pinnedBottomRow = useMemo(() => {
		const totals = SAL1030_R01List.reduce((acc, item) => ({
			orderQty: acc.orderQty + (item.orderQty || 0),
			cancelQty: acc.cancelQty + (item.cancelQty || 0),
			itemAmt: acc.itemAmt + (item.itemAmt || 0),
			shippedQty: acc.shippedQty + (item.shippedQty || 0),
			remainingAmt: acc.remainingAmt + (item.remainingAmt || 0),
		}), { orderQty: 0, cancelQty: 0, itemAmt: 0, shippedQty: 0, remainingAmt: 0 });

		return [{
			rowIndex: '',
			compNm: SAL1030_R01List.length.toLocaleString() + "건",
			orderQty: totals.orderQty,
			cancelQty: totals.cancelQty,
			itemAmt: totals.itemAmt,
			shippedQty: totals.shippedQty,
			remainingAmt: totals.remainingAmt,
		}] as any[];
	}, [SAL1030_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [SAL1030_R01List, selectedSAL1030_R01]);

	return (
		<GridContextWrapper<SAL1030_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={SAL1030_R01List as SAL1030_R01Dto[]}
			initSelected={initSelectedSAL1030_R01}
			defaultRowData={defaultSAL1030_R01Dto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL1030_R01Dto>
				ref={gridRef}
				rowData={SAL1030_R01List}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SAL1030_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedSAL1030_R01 ? [selectedSAL1030_R01.valueIndex || 0] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
